import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Axios from "services/axios";
import { goToLogin } from "services/session";
import { userLogout } from "store/actions";
import { LOGIN_SUCCESS } from "store/types/authTypes";

const SELECTED_BRANCH_KEY = "hospital-selected-branch";

/**
 * The session lives in an httpOnly cookie that JavaScript cannot read (mvp-next
 * ADR 0004), so "am I signed in?" can only be answered by asking the API.
 * `GET /me` is that question: it answers off the cookie alone and 401s when the
 * session is gone.
 *
 * The answer is deliberately three-valued. `/me` can also fail for reasons that
 * say nothing about the session — a dropped connection, a 502 — and signing a
 * user out over one is worse than the error itself, because this app's
 * unauthenticated path revokes the session for real. So only a 401 is a logout;
 * anything else leaves us UNKNOWN, the app renders on its persisted state, and
 * the next real request decides.
 *
 * @typedef {"authenticated" | "unauthenticated" | "unknown"} AuthStatus
 */
export const AUTH_STATUS = {
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
  UNKNOWN: "unknown",
};

const AuthContext = createContext({
  isAuthenticated: false,
  status: AUTH_STATUS.UNKNOWN,
  user: null,
});

/**
 * `/me` is this app's only source for the signed-in user now that the SPA owns
 * the login form, so the keys login used to write are written from it.
 *
 * @param {Record<string, any>} user
 */
const rememberSessionDetails = (user) => {
  // There is no `email` field: login is by email and the API calls it
  // `username`. `fetchLocations` and the location bootstrap read this key.
  if (user?.username) {
    localStorage.setItem("email", user.username);
  }

  if (typeof user?.consent !== "undefined") {
    localStorage.setItem("consent", JSON.stringify(user.consent));
  }

  // The server knows which location is selected, so a cleared localStorage no
  // longer forces a detour through the branch picker.
  if (!localStorage.getItem(SELECTED_BRANCH_KEY) && user?.currentLocation?._id) {
    localStorage.setItem(SELECTED_BRANCH_KEY, user.currentLocation._id);
  }

  // Seeded here rather than at login, which this app no longer performs. The
  // support prompt measures from this stamp, so without it it never appears.
  if (!localStorage.getItem("pastDate")) {
    localStorage.setItem("pastDate", JSON.stringify(new Date()));
  }
};

/**
 * @param {AuthProviderProps} props
 */
export const AuthProvider = (props) => {
  const { children } = props;
  const dispatch = useDispatch();
  const [status, setStatus] = useState(/** @type {AuthStatus} */ (AUTH_STATUS.UNKNOWN));
  const [user, setUser] = useState(/** @type {Record<string, any> | null} */ (null));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    async function bootstrapAuth() {
      try {
        const { data } = await Axios.get("/me");
        if (!isActive) return;

        const sessionUser = data?.data;

        if (!sessionUser?._id) {
          setStatus(AUTH_STATUS.UNKNOWN);
          setLoading(false);
          return;
        }

        rememberSessionDetails(sessionUser);
        setUser(sessionUser);
        setStatus(AUTH_STATUS.AUTHENTICATED);
        // `AuthRoute` gates on this, and it used to be set only by this app's
        // own login. Without it every route bounces to /signout.
        dispatch({ type: LOGIN_SUCCESS, payload: { isLoggedIn: true } });
        setLoading(false);
      } catch (error) {
        if (!isActive) return;

        if (error?.response?.status === 401) {
          setStatus(AUTH_STATUS.UNAUTHENTICATED);
          // Clear the stale persisted session, then leave for the SPA. The
          // loading gate stays up so nothing renders against dead state.
          dispatch(userLogout());
          goToLogin({ replace: true });
          return;
        }

        // eslint-disable-next-line no-console
        console.error("Failed to bootstrap the session", error);
        setStatus(AUTH_STATUS.UNKNOWN);
        setLoading(false);
      }
    }

    void bootstrapAuth();

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  const value = useMemo(
    () => ({
      isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
      status,
      user,
    }),
    [status, user],
  );

  // Children must not render until this resolves, or `AuthRoute` sees
  // `loggedIn: false` for one commit and bounces.
  return (
    <AuthContext.Provider value={value}>{!loading ? children : <div>Loading application...</div>}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export const useAuth = () => useContext(AuthContext);

/**
 * @typedef {Object} AuthProviderProps
 * @property {React.ReactNode} children
 */
